import { useState } from "react";
import { Edit3 } from "lucide-react";
import { Badge } from "@/components/atoms/studio/ui/badge";
import type { TestPlan } from "@/services/testing";
import type { PlanExecutionPreviewModel } from "@/utils/testing/planPreview";
import { describeQueryStep } from "./typedQuery";

export function PlanExecutionPreview({
	preview,
	plan,
	disabled,
	onEditStep,
}: {
	preview: PlanExecutionPreviewModel;
	plan?: TestPlan;
	disabled?: boolean;
	onEditStep?: (stepIdx: number, newSay: string) => Promise<void>;
}) {
	const [editingStepIdx, setEditingStepIdx] = useState<number | null>(null);
	const [editingStepText, setEditingStepText] = useState("");

	async function commitEdit() {
		if (editingStepIdx === null || !onEditStep) return;
		await onEditStep(editingStepIdx, editingStepText);
		setEditingStepIdx(null);
	}

	// Determine if IR steps are available for editing
	const irBody = plan?.steps as
		| { format?: string; ir?: { steps?: Array<Record<string, unknown>>; setup?: Array<Record<string, unknown>> } }
		| undefined;
	const irSteps = irBody?.format === "ir_v1" && Array.isArray(irBody.ir?.steps) ? irBody.ir!.steps : null;
	const irSetup = irSteps && Array.isArray(irBody?.ir?.setup) ? irBody!.ir!.setup! : [];

	/**
	 * The raw IR row behind a preview row. `previewFromIr` emits setup rows FIRST and then the step
	 * rows, so a preview row number maps into `setup` before it maps into `steps`. (The inline
	 * step-editor above uses the older flat mapping; this one is only used for read-only display.)
	 */
	function irRowFor(previewNumber: number): Record<string, unknown> | null {
		if (!irSteps) return null;
		const i = previewNumber - 1;
		if (i < irSetup.length) return irSetup[i] ?? null;
		return irSteps[i - irSetup.length] ?? null;
	}

	return (
		<div className="mb-2 rounded-md border border-border bg-muted/20 text-[11px]">
			<div className="flex items-center gap-2 border-b border-border px-2 py-1 font-semibold text-muted-foreground">
				<span>English plan to approve</span>
				<Badge variant="outline" className="ml-auto text-[11px]">
					{preview.source === "contract" ? "agent contract" : "inferred preview"}
				</Badge>
			</div>
			<div className="overflow-x-auto">
				<table className="w-full table-fixed">
					<thead className="bg-card/70 text-[11px] uppercase text-muted-foreground">
						<tr>
							<th className="w-7 px-2 py-1 text-left font-medium">#</th>
							<th className="px-2 py-1 text-left font-medium">Step</th>
							{/* Secondary columns are the first thing to go on a narrow chat column, rather than
							    forcing the whole table to overflow. */}
							<th className="hidden px-2 py-1 text-left font-medium group-data-[cw-md]/chat:table-cell">Target</th>
							<th className="hidden w-24 px-2 py-1 text-left font-medium group-data-[cw-md]/chat:table-cell">Type</th>
						</tr>
					</thead>
					<tbody>
						{preview.steps.map((step) => {
							// Map preview step number to IR step index (setup rows come first in previewFromIr)
							// For contract-sourced previews, irSteps is null so editing is unavailable.
							const irStepIdx = irSteps ? step.number - 1 : null;
							const isEditing = editingStepIdx !== null && editingStepIdx === irStepIdx;
							return (
							<tr key={step.number} className="group border-t border-border/40">
								<td className="px-2 py-1 font-mono text-muted-foreground">
									{step.number}
								</td>
								<td className="break-words px-2 py-1 text-foreground/90">
									{isEditing ? (
										<input
											autoFocus
											value={editingStepText}
											onChange={(e) => setEditingStepText(e.target.value)}
											onKeyDown={(e) => {
												if (e.key === "Enter") { e.preventDefault(); void commitEdit(); }
												if (e.key === "Escape") setEditingStepIdx(null);
											}}
											className="h-6 w-full border-b border-primary bg-transparent text-xs focus:outline-none"
										/>
									) : (
										<span className="flex flex-col gap-0.5">
											<span className="flex items-center gap-1">
												<span>{step.action}</span>
												{irStepIdx !== null && onEditStep && !disabled && (
													<button
														type="button"
														title="Edit step"
														aria-label={`Edit step ${step.number}`}
														onClick={() => {
															const say = irSteps ? String(irSteps[irStepIdx]?.say ?? step.action) : step.action;
															setEditingStepIdx(irStepIdx);
															setEditingStepText(say);
														}}
														className="ml-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 text-muted-foreground"
													>
														<Edit3 className="h-3 w-3" />
													</button>
												)}
											</span>
											{/* Data steps: read the structured `query` first — it is validated
											    against the org schema, so it is stronger evidence than the same
											    assertion written as a raw `soql` string. */}
											{(() => {
												const described = describeQueryStep(irRowFor(step.number));
												if (!described) return null;
												return (
													<span
														className="truncate font-mono text-[11px] text-muted-foreground"
														title={described}
													>
														{described}
													</span>
												);
											})()}
										</span>
									)}
								</td>
								<td className="hidden break-words px-2 py-1 text-muted-foreground group-data-[cw-md]/chat:table-cell">
									{step.target || "current page / generated selector"}
								</td>
								<td className="hidden px-2 py-1 group-data-[cw-md]/chat:table-cell">
									<Badge variant="outline" className="text-[11px]">
										{step.kind}
									</Badge>
								</td>
							</tr>
						); })}
					</tbody>
				</table>
			</div>
			<div className="grid gap-2 border-t border-border p-2 group-data-[cw-md]/chat:grid-cols-2 group-data-[cw-lg]/chat:grid-cols-3">
				<PreviewChipList label="Login" values={preview.login} empty="connected user" />
				<PreviewChipList label="Prerequisites" values={preview.prerequisites} empty="connected org ready" />
				<PreviewChipList label="Setup" values={preview.setup} empty="no setup declared" />
				<PreviewChipList label="Pages" values={preview.pages} empty="resolved by generated spec" />
				<PreviewChipList label="Clicks" values={preview.clicks} empty="none declared" />
				<PreviewChipList label="Fields" values={preview.fields} empty="none declared" />
				<PreviewChipList label="Objects" values={preview.objects} empty="inferred at run time" />
				<PreviewChipList label="Records" values={preview.records} empty="created/located during run" />
				<PreviewChipList label="Checks" values={preview.checks} empty="shown in generated TS" />
				<PreviewChipList label="Cleanup" values={preview.cleanup} empty="auto cleanup / none declared" />
			</div>
			{preview.unknowns.length > 0 && (
				<div className="border-t border-warning/30 bg-warning/10 px-2 py-1 text-[11px] text-warning-strong">
					Needs clarification before approval: {preview.unknowns.join("; ")}
				</div>
			)}
		</div>
	);
}

export function PreviewChipList({
	label,
	values,
	empty,
}: {
	label: string;
	values: string[];
	empty: string;
}) {
	return (
		<div className="min-w-0">
			<div className="mb-1 text-[11px] font-semibold uppercase text-muted-foreground">
				{label}
			</div>
			<div className="flex flex-wrap gap-1">
				{values.length > 0 ? (
					values.map((value) => (
						<span
							key={value}
							className="max-w-full truncate rounded bg-card px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
							title={value}
						>
							{value}
						</span>
					))
				) : (
					<span className="text-[11px] text-muted-foreground/80">{empty}</span>
				)}
			</div>
		</div>
	);
}
